import * as ActionTypes from "constants/ActionTypes";
import * as Timings from "constants/Timings";
import * as InactivityKey from "utils/interfaces/InactivityKey";
import {
  call,
  put,
  select,
  actionChannel,
  fork,
  flush,
  cancel,
  take,
  takeEvery,
} from "redux-saga/effects";
import { delay, buffers } from "redux-saga";
import { setInactivity, setTransitioning } from "actions/InactivityActions";

import find from "lodash/find";
import omit from "lodash/omit";
import pick from "lodash/pick";

//////
// Sample throttle
/////

// const takeLatestEvery = (ms, pattern, task, ...args) => fork(function * () {
//     let lastTime = new Date().getTime();
//     let lastAction;
//     // get the channel based on the pattern
//     const throttleChannel = yield actionChannel(pattern);

//     while (true) { // eslint-disable-line no-constant-condition
//         // infinite loop to pop off the next action
//         const action = yield take(throttleChannel);
//         const newTime = new Date().getTime();

//         if (lastTime && newTime - lastTime > ms) {
//             // If the timeout has been reached, grab the next one.
//             lastTime = newTime;
//             if (lastAction) {
//                 // cancel the last action if still in play
//                 yield cancel(lastAction);
//             }
//             // run the action
//             lastAction = yield fork(task, ...args, action);
//         } else {
//             // If the timeout hasn't been reached, ignore any actions in the buffer.
//             yield flush(throttleChannel);
//         }
//     }
// });

const AVG_TRANSITION_TIMING = 1000; // should cover most timings

const timeoutThrottle = (
  ms,
  heirarchicalTaskIdentifierList,
  fnIngoreCondition,
  task,
) =>
  fork(function* () {
    /*
     * This is a control function to smooth out timing action calls using a action heirarchy list.
     * 1. Wait for the next action that matches the task list
     * 2. When an action that matches is received, wait for other actions to populate for N seconds
     * 3. Evaluate the list of actions just produced to find the one that is first in the action list
     * 4. Fork that action and ignore the lesser actions
     */
    const throttleChannel = yield actionChannel(
      heirarchicalTaskIdentifierList,
      buffers.sliding(10),
    );
    let lastAction;
    while (true) {
      // Get all of the actions in the channel every n milliseconds
      const initialAction = yield take(throttleChannel);
      // check the ignore condition and continue the loop if met
      const shouldIgnore = yield select((state) =>
        fnIngoreCondition(state, initialAction),
      );
      if (shouldIgnore) {
        continue;
      }
      if (initialAction.type === ActionTypes.FORCE_INACTIVITY) {
        // wait n ms for any other actions
        yield delay(ms);
      }
      // get all actions in the channel
      const otherActions = yield flush(throttleChannel);
      const actions = [...otherActions, initialAction];
      for (let i = 0; i < heirarchicalTaskIdentifierList.length; i += 1) {
        // Look for the first matching action and use it.
        const id = heirarchicalTaskIdentifierList[i];
        const action = find(actions, { type: id });
        if (action) {
          if (lastAction) {
            // kill the last action so we don't get weird timings
            yield cancel(lastAction);
          }

          lastAction = yield fork(task, action);
          break;
        }
      }
    }
  });

const conditionalTakeLatest = (taskList, fnIgnoreCondition, task) =>
  fork(function* () {
    const channel = yield actionChannel(taskList);
    let lastAction;
    while (true) {
      const nextAction = yield take(channel);
      const shouldIgnore = yield select((state) =>
        fnIgnoreCondition(state, nextAction),
      );
      if (!shouldIgnore) {
        if (lastAction) {
          yield cancel(lastAction);
        }
        yield fork(task, nextAction);
      }
    }
  });

// const conditionalTake = (taskList, fnIgnoreCondition, task) => fork(function * () {
//     const channel = yield actionChannel(taskList);
//     while (true) { // eslint-disable-line no-constant-condition
//         const nextAction = yield take(channel);
//         const shouldIgnore = yield select(state => fnIgnoreCondition(state, nextAction));

//         if (!shouldIgnore) {
//             yield fork(task, nextAction);
//         }
//     }
// })

function* handleTiming(action, key, timeoutSeconds) {
  /*
   * Handle timing for a specific timeout.
   */
  if (action.type === ActionTypes.FORCE_INACTIVITY) {
    yield put(setInactivity(key, true));
  } else {
    yield put(setInactivity(key, false));
    yield call(delay, timeoutSeconds * 1000);
    yield put(setInactivity(key, true));
  }
}

function* handleControlsTiming(action) {
  /*
   * Filter activity through the following funnel.
   * 1. If a control toggled inactivity and receive a FORCE_INACTIVITY
   *   then set the main control state to inactive and set the timeout for the compact controls
   * 2. If REPORT_ACTIVITY is sent then that should activate all control set timeouts.
   * 3. If REPORT_UI_ACTIVITY is sent then only secondary controls should be reset.
   */

  const mainControlsTimeout =
    Timings.INACTIVITY_TIMINGS[InactivityKey.CONTROLS];
  const compactControlsTimeout =
    Timings.SECOND_LEVEL_INACTIVITY_TIMINGS[InactivityKey.COMPACT_CONTROLS];

  if (action.type === ActionTypes.FORCE_INACTIVITY) {
    // Close main controls
    yield fork(
      handleTiming,
      {},
      InactivityKey.COMPACT_CONTROLS,
      compactControlsTimeout,
    );
    yield put(setInactivity(InactivityKey.CONTROLS, true));
  } else if (action.type === ActionTypes.REPORT_ACTIVITY) {
    // Trigger all controls to reset
    yield fork(handleTiming, {}, InactivityKey.CONTROLS, mainControlsTimeout);
    yield fork(
      handleTiming,
      {},
      InactivityKey.COMPACT_CONTROLS,
      compactControlsTimeout,
    );
  } else if (action.type === ActionTypes.REPORT_UI_ACTIVITY) {
    // Trigger only secondary controls to reset
    const controlsAreActive = yield select(
      (state) => !state.inactivity.controls,
    );
    if (controlsAreActive) {
      // In the event that the main controls are still active then trigger them again.
      yield fork(handleTiming, {}, InactivityKey.CONTROLS, mainControlsTimeout);
    }
    yield fork(
      handleTiming,
      {},
      InactivityKey.COMPACT_CONTROLS,
      compactControlsTimeout,
    );
  }
}

function getActivityHandler(key, timeoutSeconds) {
  /*
   * Generate a handler generator function with a specific timeout
   */
  return function* timingHandler(action) {
    yield call(handleTiming, action, key, timeoutSeconds);
  };
}

function* handleTransitionCheck(action) {
  const isTransitioning = yield select(
    (state) => state.inactivity.isTransitioning,
  );
  const timingsToCheck = [
    "controls",
    "interviewSidebar",
    "loopingVideos",
    "compactControls",
  ];
  const timings = yield select((state) => state.inactivity);
  const valuesToCheck = pick(timings, timingsToCheck);
  let willTransition = false;
  for (const key in Object.keys(valuesToCheck)) {
    if (action.type === ActionTypes.FORCE_INACTIVITY && !valuesToCheck[key]) {
      willTransition = true;
    }
    if (
      action.type === ActionTypes.REPORT_ACTIVITY ||
      (action.type === ActionTypes.REPORT_UI_ACTIVITY && valuesToCheck[key])
    ) {
      willTransition = true;
    }
  }

  if (willTransition && !isTransitioning) {
    // lock for a transition period
    yield put(setTransitioning(true));
    yield delay(AVG_TRANSITION_TIMING);
    yield put(setTransitioning(false));
  }
}

export default function* inactivitySaga() {
  /******
   *   With desktop controls we want both top level and secondary inactivity to be reset each
   *   time either activity or ui activity is reported.
   *
   *   With mobile controls we want top level inactivity to be reset only when REPORT_ACTIVITY
   *   is fired.  Secondary timeouts are reset in both cases.
   *******/

  let dimensions = yield select((state) => state.viewport.dimensions);
  // Wait for the player to load size before proceeding.
  while (dimensions.height === 0 || dimensions.width === 0) {
    yield call(delay, 500);
    dimensions = yield select((state) => state.viewport.dimensions);
  }

  const THROTTLE_MS = 200;

  const ignoreActionConditional = (state) => state.inactivity.isTransitioning;

  // Setup all normal timers except controls
  for (const key in omit(Timings.INACTIVITY_TOUCH_TIMINGS, [
    InactivityKey.CONTROLS,
  ])) {
    yield timeoutThrottle(
      THROTTLE_MS,
      [ActionTypes.FORCE_INACTIVITY, ActionTypes.REPORT_ACTIVITY],
      ignoreActionConditional,
      getActivityHandler(key, Timings.INACTIVITY_TIMINGS[key]),
    );
  }

  // Setup second level timings except compact and interview
  const secondLevelTimings = omit(Timings.SECOND_LEVEL_INACTIVITY_TIMINGS, [
    InactivityKey.COMPACT_CONTROLS,
    InactivityKey.INTERVIEW_SIDEBAR,
  ]);

  // Second level timings
  for (const key in secondLevelTimings) {
    yield conditionalTakeLatest(
      [ActionTypes.REPORT_ACTIVITY, ActionTypes.REPORT_UI_ACTIVITY],
      ignoreActionConditional,
      getActivityHandler(key, Timings.SECOND_LEVEL_INACTIVITY_TIMINGS[key]),
    );
  }

  // Use throttle for interview timing
  yield timeoutThrottle(
    THROTTLE_MS,
    [
      ActionTypes.FORCE_INACTIVITY,
      ActionTypes.REPORT_ACTIVITY,
      ActionTypes.REPORT_UI_ACTIVITY,
    ],
    ignoreActionConditional,
    getActivityHandler(
      InactivityKey.INTERVIEW_SIDEBAR,
      Timings.SECOND_LEVEL_INACTIVITY_TIMINGS[InactivityKey.INTERVIEW_SIDEBAR],
    ),
  );

  // Throttle control timings
  yield timeoutThrottle(
    THROTTLE_MS,
    [
      ActionTypes.FORCE_INACTIVITY,
      ActionTypes.REPORT_ACTIVITY,
      ActionTypes.REPORT_UI_ACTIVITY,
    ],
    ignoreActionConditional,
    handleControlsTiming,
  );

  yield takeEvery(
    //THROTTLE_MS,
    [
      ActionTypes.FORCE_INACTIVITY,
      ActionTypes.REPORT_ACTIVITY,
      ActionTypes.REPORT_UI_ACTIVITY,
    ],
    //() => false,
    handleTransitionCheck,
  );
}
